import { Alert, AlertDescription, AlertTitle } from '@tempo-io/sui-alert';
import { cn } from '@tempo-io/sui-class-utils';
import { Info } from '@tempo-io/sui-icons';
import { Separator } from '@tempo-io/sui-separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@tempo-io/sui-tooltip';
import { parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import type { SchedulingValidation } from '@/common/api/types/planner/timelineItem';
import { formatLocalDate } from '@/common/utils/dateFormats';
import { DeadlineRiskMessage } from './DeadlineRiskMessage';
import { getSchedulingValidationMessage } from './getSchedulingValidationMessage';
import { TooltipScheduleBar } from './TooltipScheduleBar';

const FieldInfoTooltip = ({ content }: { content: string }) => (
  <Tooltip>
    <TooltipTrigger className="flex items-center">
      <Info className="w-3.5 h-3.5 text-text-neutral-secondary hover:text-text-neutral-primary" />
    </TooltipTrigger>
    <TooltipContent className="max-w-56">{content}</TooltipContent>
  </Tooltip>
);

const AtRiskMessage = ({
  isEmpty,
  schedulingValidations,
  forecastedFinish,
  deadline,
  deadlineSourceItemName,
}: {
  isEmpty: boolean;
  schedulingValidations: SchedulingValidation[] | null | undefined;
  forecastedFinish: string | undefined;
  deadline: string | null | undefined;
  deadlineSourceItemName: string | null | undefined;
}) => {
  const { t } = useTranslation();

  if (isEmpty && schedulingValidations != null && schedulingValidations.length > 0) {
    return (
      <ul className="space-y-1 list-none">
        {schedulingValidations.map((validation) => (
          <li className="text-wrap" key={validation}>
            {getSchedulingValidationMessage(validation, t)}
          </li>
        ))}
      </ul>
    );
  }

  if (isEmpty) {
    return (
      <span>{t(($) => $.adaptivePlanner.scenarioDetails.table.scheduleBar.tooltip.empty)}</span>
    );
  }

  return (
    <DeadlineRiskMessage
      forecastedFinish={forecastedFinish}
      deadline={deadline}
      sourceItemName={deadlineSourceItemName}
    />
  );
};

interface TooltipDatesSectionProps {
  forecastedStart?: string;
  forecastedFinish?: string;
  latestFinish?: string;
  deadline?: string | null;
  deadlineSourceItemName?: string | null;
  isAtRisk: boolean;
  schedulingValidations?: SchedulingValidation[] | null;
  showFieldTooltips?: boolean;
}

export const TooltipDatesSection = ({
  forecastedStart,
  forecastedFinish,
  latestFinish,
  deadline,
  deadlineSourceItemName,
  isAtRisk,
  schedulingValidations,
  showFieldTooltips = false,
}: TooltipDatesSectionProps) => {
  const { t } = useTranslation();
  const isEmpty = isAtRisk && !forecastedStart && !forecastedFinish && !latestFinish;

  return (
    <div className="space-y-2">
      {forecastedStart && forecastedFinish && (
        <TooltipScheduleBar
          forecastedStart={forecastedStart}
          forecastedFinish={forecastedFinish}
          latestFinish={latestFinish}
          isAtRisk={isAtRisk}
        />
      )}
      <div className="flex justify-between">
        {forecastedStart && (
          <div className="flex flex-col">
            <span className="flex items-center gap-1 text-xs font-medium">
              {t(($) => $.adaptivePlanner.scenarioDetails.forecastedStart)}:
              {showFieldTooltips && (
                <FieldInfoTooltip
                  content={t(($) => $.adaptivePlanner.scenarioDetails.forecastedStartTooltip)}
                />
              )}
            </span>
            <span className="text-xs text-text-neutral-secondary">
              {formatLocalDate(parseISO(forecastedStart).toISOString())}
            </span>
          </div>
        )}

        {forecastedFinish && (
          <div className="flex flex-col items-end">
            <span className="flex items-center gap-1 text-xs font-medium">
              {t(($) => $.adaptivePlanner.scenarioDetails.forecastedFinish)}:
              {showFieldTooltips && (
                <FieldInfoTooltip
                  content={t(($) => $.adaptivePlanner.scenarioDetails.forecastedFinishTooltip)}
                />
              )}
            </span>
            <span
              className={cn(
                'text-xs',
                isAtRisk ? 'font-bold text-text-error' : 'text-text-neutral-secondary',
              )}
            >
              {formatLocalDate(parseISO(forecastedFinish).toISOString())}
            </span>
          </div>
        )}
      </div>
      {latestFinish && (
        <div className="flex w-full justify-end">
          <div className="flex flex-col items-end">
            <span className="flex items-center gap-1 text-xs font-medium">
              {t(($) => $.adaptivePlanner.scenarioDetails.latestFinish)}:
              {showFieldTooltips && (
                <FieldInfoTooltip
                  content={t(($) => $.adaptivePlanner.scenarioDetails.latestFinishTooltip)}
                />
              )}
            </span>
            <span className="text-xs text-text-neutral-secondary">
              {formatLocalDate(parseISO(latestFinish).toISOString())}
            </span>
          </div>
        </div>
      )}
      {isAtRisk && latestFinish && <Separator />}
      {isAtRisk && (
        <Alert variant="error">
          <AlertTitle>{t(($) => $.loopVisualization.timelineTooltip.atRisk)}</AlertTitle>
          <AlertDescription>
            <AtRiskMessage
              isEmpty={isEmpty}
              schedulingValidations={schedulingValidations}
              forecastedFinish={forecastedFinish}
              deadline={deadline}
              deadlineSourceItemName={deadlineSourceItemName}
            />
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};
